import { shallowRef } from 'vue'

import type { PlannerEndpoint } from '@/components/plan/plannerEndpoint'

const STORAGE_KEY = 'hanoi-bus:route-history'
const MAX_ENTRIES = 10

export interface RouteHistoryEntry {
  start: PlannerEndpoint
  end: PlannerEndpoint
}

function isEndpoint(value: unknown): value is PlannerEndpoint {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.label === 'string' &&
    typeof candidate.lat === 'number' &&
    typeof candidate.lng === 'number' &&
    Number.isFinite(candidate.lat) &&
    Number.isFinite(candidate.lng)
  )
}

/**
 * `localStorage` is user-writable and may hold a value from an older build, so
 * every entry is validated rather than trusted. A corrupt store must degrade to
 * "no history", never throw during app start — private mode and hand-edited
 * storage both produce this.
 */
function read(): RouteHistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (entry): entry is RouteHistoryEntry =>
          Boolean(entry) &&
          typeof entry === 'object' &&
          isEndpoint((entry as RouteHistoryEntry).start) &&
          isEndpoint((entry as RouteHistoryEntry).end),
      )
      .slice(0, MAX_ENTRIES)
  } catch {
    return []
  }
}

const entries = shallowRef<RouteHistoryEntry[]>(read())

function key(entry: RouteHistoryEntry) {
  return `${entry.start.lat},${entry.start.lng}>${entry.end.lat},${entry.end.lng}`
}

export function useRouteHistory() {
  function remember(entry: RouteHistoryEntry) {
    const deduped = entries.value.filter((existing) => key(existing) !== key(entry))
    const next = [entry, ...deduped].slice(0, MAX_ENTRIES)
    entries.value = next
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Storage full or blocked. The in-memory list still works for this session.
    }
  }

  function clear() {
    entries.value = []
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* nothing to do */
    }
  }

  return { entries, remember, clear }
}
